import { BehaviorSubject, map, Observable, of, Subscription } from 'rxjs';
import { inject, Injectable, Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { HttpClient } from '@angular/common/http';
import { Transaction } from '@elastic/apm-rum';
import { ThemeConfig } from '../../../../assets/data/enum/themeConfig';
import { UiMode } from '../../../../assets/data/enum/uiMode';
import { LivestockCoreDataService } from '../livestockCoreData.service';
import { ApmEventsService } from '../apmEvents.service';

declare let jQuery: any;
declare let google: any;

@Injectable({
  providedIn: 'root',
})
export class GeneralUtilsService {
  private livestockDataSubscription: Subscription = new Subscription();

  //Service
  translate = inject(TranslateService);
  http = inject(HttpClient);
  private injector = inject(Injector);
  apmEventsService = inject(ApmEventsService);

  isMobileResolution: boolean;
  private showFinanceTabBarMenu: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private showIrrigatingTabBarMenu: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private showDiseasesTabBarMenu: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private showInsectsTabBarMenu: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private showCropPlanningTabBarMenu: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private showBatchTabBarMenu: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private IPLookUpData: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  progressBarSyncData: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  splashTx: Transaction | null = null;
  private showSplashScreenComponentSubject = new BehaviorSubject<boolean>(false);
  showSplashScreenComponent$ = this.showSplashScreenComponentSubject.asObservable();
  themeConfig = ThemeConfig;
  globalUIMode: BehaviorSubject<UiMode> = new BehaviorSubject<UiMode>(
    this.themeConfig.defaultUIMode as UiMode
  );
  refreshwebAPP = true;
  translatedLabel: Array<Array<any>> = [];
  stringArrayForCalendar2 = [
    'diseases.zoneDetail.label_today',
    'yesterday',
    'multifarm.last_7_days',
    'weather.last30Days',
    'financial.dashboard.label_this_month',
    'financial.dashboard.label_last_month',
    'year_to_date',
  ];

  loadingCompleted: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  UiMode = UiMode;

  constructor() {
    this.isMobileResolution = window.innerWidth < 768;
  }

  getGlobalUIModeByRoute = (urlRoute): Observable<UiMode> => {
    const listRouteForced = [
      {
        route: 'silos',
        UImode: this.UiMode.FARM,
      },
      {
        route: 'containerActivities',
        UImode: this.UiMode.FARM,
      },
      {
        route: 'rations/dashboard',
        UImode: this.UiMode.FARM,
      },
    ];
    const foundRoute = listRouteForced.find((item) => urlRoute.includes(item.route));
    if (foundRoute && foundRoute.UImode) {
      return new Observable((observer) => {
        observer.next(foundRoute.UImode);
        observer.complete();
      });
    } else {
      return this.globalUIMode.asObservable();
    }
  };

  updateGlobalUIMode(mode: UiMode) {
    // 0. Unsubscribe from any previous livestock data subscription to avoid memory leaks
    this.livestockDataSubscription?.unsubscribe();

    // 1. Update the UI mode immediately
    this.globalUIMode.next(mode);

    // 2. If the new mode is LIVESTOCK, trigger the data load in the background.
    if (mode === UiMode.LIVESTOCK) {
      const livestockCoreDataService = this.injector.get(LivestockCoreDataService);
      this.livestockDataSubscription = livestockCoreDataService.loadAllData().subscribe({
        error: (err) => console.error('Livestock data load failed', err),
      });
    }
  }

  getshowFinanceTabBarMenu = () => {
    return this.showFinanceTabBarMenu.asObservable();
  };

  updateshowFinanceTabBarMenu(bool: boolean) {
    this.showFinanceTabBarMenu.next(bool);
  }

  getshowIrrigatingTabBarMenu = () => {
    return this.showIrrigatingTabBarMenu.asObservable();
  };

  getshowDiseasesTabBarMenu = () => {
    return this.showDiseasesTabBarMenu.asObservable();
  };

  updateshowDiseasesTabBarMenu(bool: boolean) {
    this.showDiseasesTabBarMenu.next(bool);
  }

  getshowInsectsTabBarMenu = () => {
    return this.showInsectsTabBarMenu.asObservable();
  };

  updateshowInsectsTabBarMenu(bool: boolean) {
    this.showInsectsTabBarMenu.next(bool);
  }

  getshowCropPlanningTabBarMenu = () => {
    return this.showCropPlanningTabBarMenu.asObservable();
  };

  updateshowBatchTabBarMenu(bool: boolean) {
    this.showBatchTabBarMenu.next(bool);
  }

  getshowBatchTabBarMenu = () => {
    return this.showBatchTabBarMenu.asObservable();
  };

  updateshowCropPlanningTabBarMenu(bool: boolean) {
    this.showCropPlanningTabBarMenu.next(bool);
  }

  stripDate(ds: Date): Date {
    if (ds) {
      const d = new Date(ds);
      const y = d.getFullYear();
      const m = this.addZero(d.getMonth() + 1);
      const dd = this.addZero(d.getDate());
      const hh = d.getHours();
      const mm = d.getMinutes();

      const dateReturn = new Date(`${y}-${m}-${dd}`);
      dateReturn.setHours(hh);
      dateReturn.setMinutes(mm);

      return dateReturn;
    } else {
      return null;
    }
  }

  addZero(v: number): string {
    return v < 10 ? '0' + v : v.toString();
  }

  roundCustom(value: number, decimals: number) {
    return Number(
      Math.round(Number(value.toString() + 'e' + decimals.toString())) + 'e-' + decimals
    );
  }

  roundCustom2(value: number, decimals: number) {
    return Number(
      Math.round(parseFloat(value + 'e' + decimals)) + 'e-' + decimals.toFixed(decimals)
    );
  }

  parseAndFormatNumber(value: string | number, decimalPlaces: number): number | string {
    const numericValue = typeof value === 'string' ? parseFloat(value.replace(',', '')) : value;

    if (!isNaN(numericValue) && isFinite(numericValue)) {
      const formattedNumber = numericValue.toLocaleString('it-IT', {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      });
      return formattedNumber;
    } else {
      return ''; // o un valore di fallback se la conversione non riesce
    }
  }

  getDatepickerRangesTranslations() {
    this.translatedLabel = [];
    this.stringArrayForCalendar2.forEach((singleElement, index) => {
      this.translate.get(singleElement).subscribe((text: string) => {
        singleElement = text;
        const startOfDay = moment().startOf('day').hour(0).minute(0).second(0);
        const endOfDay = moment().endOf('day').hour(23).minute(59).second(59);
        switch (index) {
          case 0:
            this.translatedLabel.push([singleElement, [startOfDay, endOfDay]]);
            break;
          case 1:
            this.translatedLabel.push([
              singleElement,
              [moment(startOfDay).subtract(1, 'days'), moment(endOfDay).subtract(1, 'days')],
            ]);
            break;
          case 2:
            this.translatedLabel.push([
              singleElement,
              [moment(startOfDay).subtract(6, 'days'), moment(endOfDay)],
            ]);
            break;
          case 3:
            this.translatedLabel.push([
              singleElement,
              [moment(startOfDay).subtract(29, 'days'), moment(endOfDay)],
            ]);
            break;
          case 4:
            this.translatedLabel.push([
              singleElement,
              [moment(startOfDay).startOf('month'), moment(endOfDay).endOf('month')],
            ]);
            break;
          case 5:
            this.translatedLabel.push([
              singleElement,
              [
                moment(startOfDay).subtract(1, 'month').startOf('month'),
                moment(endOfDay).subtract(1, 'month').endOf('month'),
              ],
            ]);
            break;
          case 6:
            this.translatedLabel.push([
              singleElement,
              [moment(startOfDay).startOf('year'), moment().add(1, 'days')],
            ]);
            break;
          default:
        }
      });
    });
    const obj = this.translatedLabel.reduce(function (o, currentArray) {
      const key = currentArray[0],
        value = currentArray[1];
      o[key] = value;
      return o;
    }, {});
    return obj;
  }

  // Returns a formatted HTML string with the atomic substript from a chemical element
  formatChemicalElement(element: string, useHtml: boolean = true) {
    // Check if the string contains numbers
    if (!/\d/.test(element)) {
      // If it doesn't contain numbers, return the original string
      return element;
    }

    if (useHtml) {
      // If it contains numbers, use a regex to find all numbers and wrap them with <sub> tags
      return element.replace(/(\d+)/g, '<sub>$1</sub>');
    } else {
      // Mapping of numbers to their Unicode subscript counterparts
      const subscriptNumbers = {
        '0': '₀',
        '1': '₁',
        '2': '₂',
        '3': '₃',
        '4': '₄',
        '5': '₅',
        '6': '₆',
        '7': '₇',
        '8': '₈',
        '9': '₉',
      };
      // Replace each digit with its subscript counterpart
      return element
        .split('')
        .map((char) => subscriptNumbers[char] || char)
        .join('');
    }
  }

  getDatepickerLocaleTranslations() {
    const months = [
      'january',
      'february',
      'march',
      'april',
      'may',
      'june',
      'july',
      'august',
      'september',
      'october',
      'november',
      'december',
    ];

    const locale = {
      format: 'DD-MM-YYYY',
      monthNames: months.map((m) => this.translate.instant(`months.${m}`)),
      daysOfWeek: Object.keys(this.translate.instant('truncatedDays')).map((d) =>
        this.translate.instant(`truncatedDays.${d}`)
      ),
      firstDay: 1,
      applyLabel: this.translate.instant('contractManagement.common.chain_filters_apply', {}),
      cancelLabel: this.translate.instant('contractManagement.common.abort', {}),
      weekLabel: '',
    };

    return locale;
  }

  roundToDecimals(num, decimals) {
    const result = 0.0;
    const resultTmp = +(Math.round(Number(num + 'e+' + decimals)) + 'e-' + decimals);
    return resultTmp;
  }

  getFileExtension(filename) {
    return /[.]/.exec(filename) ? /[^.]+$/.exec(filename)[0] : undefined;
  }

  getUniqValues = (list, match) => {
    // Map the values based on the match key
    const mappedValues = list.map((item) => item[match]);

    // Remove duplicates
    const uniqueValues = Array.from(new Set(mappedValues));

    // Sort the array
    return uniqueValues.sort();
  };

  getLanguage() {
    return this.getCorrectLanguageCode(localStorage.getItem('crntLng'));
  }

  getCorrectLanguageCode(lang: string) {
    if (lang === 'br') {
      return 'pt-br';
    }
    return lang;
  }

  /**
   * Generates a string with specified number of digits and characters
   *
   * @param {length} number - the number of digits
   * @returns {string} - Returns a random string
   */
  makeRandoId = (length) => {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    // tslint:disable-next-line:prefer-const
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  };

  uuidv4(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0,
        v = c == 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  getRandomColor = (): string => {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  };

  getMultipleRandomColor = (numberOfCOlor): Array<string> => {
    const colors = [];
    for (let i = 0; i < numberOfCOlor; i++) {
      colors.push(this.getRandomColor());
    }
    return colors;
  };

  getUnique(arr, comp) {
    const unique = arr
      .map((e) => e[comp])
      .map((e, i, final) => final.indexOf(e) === i && i)
      .filter((e) => arr[e])
      .map((e) => arr[e]);
    return unique;
  }

  arrayIsNull(inputArray) {
    if (inputArray.length) {
      let currentElement = inputArray[0];
      for (let i = 1, len = inputArray.length; i < len && currentElement === null; i += 1) {
        currentElement = currentElement || inputArray[i];
      }
      if (currentElement !== null) {
        return false;
      }
    }
    return true;
  }

  fromArrayToKeyValue = (data): Array<any> => {
    // tslint:disable-next-line:prefer-const
    const dataList = [];
    data.forEach((element) => {
      // tslint:disable-next-line:prefer-const
      const payload = {
        key: element.parameterName,
        value: element.value,
      };
      dataList.push(payload);
    });

    return dataList;
  };

  hideChatSupportElement = () => {
    const chatSupport = document.getElementById('wp-intercom-slack');
    if (chatSupport) {
      const wpicFrame = chatSupport.querySelector('.wpic-frame');
      if (wpicFrame && !wpicFrame.classList.contains('active')) {
        // tslint:disable-next-line:prefer-const
        chatSupport.style.opacity = '0';
      } else {
      }
    }
  };

  showChatSupportElement = () => {
    // tslint:disable-next-line:prefer-const
    const chatSupport = document.getElementById('wp-intercom-slack');
    if (chatSupport) {
      chatSupport.style.display = 'block';
      chatSupport.style.opacity = '1';
    }
  };

  clickChartSupport(forceOpen: boolean = false) {
    if (forceOpen === true) {
      this.showChatSupportElement();
    }
    jQuery('#wp-intercom-slack .wpic-launcher').click();
  }

  _base64ToArrayBuffer(base64) {
    // tslint:disable-next-line:prefer-const
    const binary_string = window.atob(base64);
    // tslint:disable-next-line:prefer-const
    const len = binary_string.length;
    // tslint:disable-next-line:prefer-const
    const bytes = new Uint8Array(len);
    // tslint:disable-next-line:prefer-const
    for (let i = 0; i < len; i++) {
      bytes[i] = binary_string.charCodeAt(i);
    }
    return bytes.buffer;
  }

  addSelectedParam = (list: Array<any>, selectedBool: boolean): Array<any> => {
    // tslint:disable-next-line:prefer-const
    const arrayWithSelect = [];
    if (list && list.length > 0) {
      list.forEach((element: any) => {
        element.selected = selectedBool;
        arrayWithSelect.push(element);
      });
    }
    return arrayWithSelect;
  };

  public getIsMobileResolution(): boolean {
    return this.isMobileResolution;
  }

  calculatePercentage(tot, num, toFixedNumber): number {
    return +((num / tot) * 100).toFixed(toFixedNumber);
  }

  getRandomNumber(min, max) {
    return Math.floor(Math.random() * (max - min) + min);
  }

  removeStoreDataFromFilterToLocalStorage = (key, value) => {
    const storedJSON = JSON.parse(localStorage.getItem(key) || '[]'); // Added a fallback to an empty array
    value = JSON.parse(value).itemName;
    // Filter out the items that don't match the value
    const filteredTag = storedJSON.filter((tag) => tag.itemName !== value);

    // Remove the item from localStorage if it exists
    localStorage.removeItem(key);
    // If there are items left after filtering, update the localStorage with the new array
    if (filteredTag.length > 0) {
      localStorage.setItem(key, JSON.stringify(filteredTag));
    }
  };

  setStoreDataFromFilterToLocalStorage = (key, value) => {
    localStorage.setItem(key, null);
    localStorage.setItem(key, value);
  };

  getStoreDataFromFilterToLocalStorage = (key) => {
    return localStorage.getItem(key);
  };

  clearStringAfterCharacter(string, character): string {
    return string.split(character)[0];
  }

  getValidateJsonString = (str): boolean => {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  };

  getCurrentCompanyId() {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    return companySelected ? companySelected.id : null;
  }

  getCompanyId() {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    return companySelected ? companySelected.companyId : null;
  }

  getLoggedInCompanyId() {
    const companyLoggedIn = localStorage.getItem('companyLoggedIn');
    return companyLoggedIn ? Number.parseInt(companyLoggedIn) : null;
  }

  // getCenterByFieldList = (fieldList: Array<Field>) => {
  //     let arrcenter = [];
  //     if (fieldList && fieldList.length > 0) {
  //         fieldList.forEach(field => {
  //             const ccc = JSON.parse(field.geoJsonArea);
  //             const coords = ccc.features[0].geometry.coordinates[0];
  //             this.mapsAPILoader.load().then(() => {
  //                 // tslint:disable-next-line: prefer-const
  //                 let bounds = new google.maps.LatLngBounds();
  //                 for (let i = 0; i < coords.length; i++) {
  //                     bounds.extend(new google.maps.LatLng(coords[i][0], coords[i][1]));
  //                 }
  //                 arrcenter[0] = bounds.getCenter().lat();
  //                 arrcenter[1] = bounds.getCenter().lng();
  //             });

  //         });
  //     }
  //     return arrcenter;
  // }

  getBoundsOfCircles(circlesList) {
    const circleBounds = new google.maps.LatLngBounds();
    if (circlesList.length == 0) {
      return circleBounds;
    }
    circlesList.forEach((c) => {
      const circle = new google.maps.Circle({
        center: { lat: c.lat, lng: c.lng },
        radius: c.radius,
      });
      circleBounds.union(circle.getBounds());
    });
    return circleBounds;
  }

  getMobileOS = () => {
    const ua = navigator.userAgent;
    if (/android/i.test(ua)) {
      return 'Android';
    } else if (/iPad|iPhone|iPod/.test(ua)) {
      return 'iOS';
    }
    return 'Other';
  };

  removePaddingSidebar = () => {
    const element = document.querySelector('.ng-sidebar__content') as HTMLElement;
    if (element) {
      element.style.padding = '0px 0px 0px 0px';
    }
  };

  // Helper to split string into number and text parts for comparison
  splitAlphaNum(str: string): (string | number)[] {
    return str
      .split(/(\d+)/)
      .filter(Boolean)
      .map((part) => {
        const num = Number(part);
        return isNaN(num) ? part.toLowerCase() : num;
      });
  }

  // Sort array of alphanumeric strings
  sortAlphanumeric(arr: string[]): string[] {
    return arr.sort((a, b) => {
      const partsA = this.splitAlphaNum(a);
      const partsB = this.splitAlphaNum(b);

      const len = Math.min(partsA.length, partsB.length);
      for (let i = 0; i < len; i++) {
        const partA = partsA[i];
        const partB = partsB[i];

        if (partA !== partB) {
          if (typeof partA === 'number' && typeof partB === 'number') {
            return partA - partB;
          }
          return partA < partB ? -1 : 1;
        }
      }
      return partsA.length - partsB.length;
    });
  }

  // Sort array of objects by specified string property alphanumerically
  sortObjectsAlphanumeric<T>(arr: T[], property: keyof T): T[] {
    return arr.slice().sort((a, b) => {
      return this.sortAlphanumeric([String(a[property]), String(b[property])])[0] ===
        String(a[property])
        ? -1
        : 1;
    });
  }

  /**
   * Method that returns the color value of a CSS variable
   * @param variableName
   * @returns {string}
   * @example getColorFromCSSVariable('--p-xFarm-xColor-400')
   */

  getColorFromCSSVariable(variableName) {
    // Get the value of the CSS variable from the :root element
    const colorValue = getComputedStyle(document.documentElement).getPropertyValue(variableName);

    // Return the color value, trimming any whitespace
    return colorValue.trim();
  }

  /**
   * Returns the last part of a string after the last dot if a dot is present
   * @param str
   * @returns {string}
   * @example getStringAfterLastDot('example.com') // 'com'
   */

  getStringAfterLastDot(str) {
    if (str.indexOf('.') !== -1) {
      return str.substring(str.lastIndexOf('.') + 1);
    } else {
      return '';
    }
  }

  getUserIPLookUp(): Observable<any> {
    const iplkp = localStorage.getItem('iplkp');
    if (iplkp && iplkp !== '' && iplkp !== undefined) {
      const IPResponse = JSON.parse(iplkp);
      if (IPResponse?.latitude) {
        IPResponse.latitude = Number(IPResponse.latitude);
      }
      if (IPResponse?.longitude) {
        IPResponse.longitude = Number(IPResponse.longitude);
      }
      this.setDataIPLookUp(IPResponse);
      return of(IPResponse);
    } else {
      return this.http
        .get<any>('https://api.ipapi.com/api/check?access_key=de4b5ff4db45d0a9db1762776f48d04f')
        .pipe(
          map((response: any) => {
            if (response) {
              const IPResponse = response;
              if (IPResponse && IPResponse.latitude) {
                IPResponse.latitude = Number(IPResponse.latitude);
              }
              if (IPResponse && IPResponse.longitude) {
                IPResponse.longitude = Number(IPResponse.longitude);
              }
              this.setDataIPLookUp(IPResponse);
              return IPResponse;
            }
          })
        );
    }
  }

  generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0,
        v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Method that checks if a string contains only digits
   * @param value
   * @returns {boolean}
   * @example isDigitsOnly('123') // true
   */
  isDigitsOnly(value: string): boolean {
    return /^\d+$/.test(value);
  }

  /**
   * Transforms a number or string by trimming its decimals according to specified rules.
   * @param {number | string} value - The input value to be transformed.
   * @returns {string} - The transformed string with trimmed decimals.
   */
  smartDecimalRound(value) {
    // Handle null or undefined values gracefully
    if (value === null || value === undefined) {
      return '';
    }

    // Convert the value to a number for processing
    const number = parseFloat(value.toString());

    // Convert the number to a string and split into integer and decimal parts
    const str = number.toString();
    const parts = str.split('.');

    // If there's no decimal part, return the integer part as a string
    if (parts.length === 1) {
      return parts[0];
    }

    const integerPart = parts[0];
    const decimalPart = parts[1];

    // Handle the case where the integer part is zero and the decimal part has leading zeros
    if (integerPart === '0' && /^0+/.test(decimalPart)) {
      // Keep only the first two significant digits of the decimal part
      const match = decimalPart.match(/^0*(\d{0,2})/);
      return '0.' + match[1];
    }

    // Otherwise, show up to two decimal places, and remove any trailing zeros
    return parseFloat(number.toFixed(2))
      .toString()
      .replace(/\.?0+$/, '');
  }

  getDataIPLookUp = () => {
    return this.IPLookUpData.asObservable();
  };

  setDataIPLookUp(data: any) {
    this.IPLookUpData.next(data);
    localStorage.setItem('iplkp', JSON.stringify(data));
  }

  isTokenExpired(token: string): boolean {
    if (!token) {
      return true; // Token mancante, considera scaduto
    }

    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) {
      return true; // Formato token JWT non valido, considera scaduto
    }

    const base64 = tokenParts[1].replace('-', '+').replace('_', '/');
    const tokenPayload = JSON.parse(atob(base64));
    const expirationDate = new Date(tokenPayload.exp * 1000); // Converte da secondi a millisecondi

    return expirationDate <= new Date(); // Restituisce true se la data di scadenza è antecedente alla data corrente
  }

  /**
   * Triggers a file download from the given URL. If a fileName is provided, it will replace the original name
   * while keeping the original file extension.
   *
   * @param url - The URL of the file to download.
   * @param fileName - Optional. If provided, this name will replace the filename in the URL, keeping the original extension.
   */
  triggerFileDownloadFromUrl(url: string, fileName?: string): void {
    // Extract the original filename and extension from the URL
    const originalFileName = url.split('/').pop() || '';
    const extension = originalFileName.includes('.') ? originalFileName.split('.').pop() : '';

    // If a custom fileName is provided, append the original extension; otherwise, use the original filename
    const finalFileName = fileName ? `${fileName}.${extension}` : originalFileName;

    const link = document.createElement('a');
    link.href = url;
    link.download = finalFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link); // Clean up
  }

  /**
   * Recursively merges properties from the `source` object into the `target` object.
   *
   * - Nested objects are merged deeply.
   * - Primitive values and arrays in the `source` overwrite those in the `target`.
   * - The `target` object is mutated and also returned.
   *
   * @param {any} target - The target object to merge properties into.
   * @param {any} source - The source object whose properties will be merged into the target.
   * @returns {any} The updated target object after merging.
   */
  deepMerge(target: any, source: any): any {
    for (const key of Object.keys(source)) {
      if (source[key] instanceof Object && key in target && target[key] instanceof Object) {
        this.deepMerge(target[key], source[key]);
      } else {
        target[key] = source[key];
      }
    }
    return target;
  }

  setShowSplashScreenComponent(state: boolean) {
    this.showSplashScreenComponentSubject.next(state);

    if (ThemeConfig.enableApm) {
      if (state && !this.splashTx) {
        // Opening splash → start transaction
        this.splashTx = this.apmEventsService.startCustomTransaction('splash-screen');
      } else if (!state && this.splashTx) {
        // Close the splash transaction if it exists
        this.apmEventsService.endCustomTransaction(this.splashTx);
        this.splashTx = null;
      }
    }
  }

  /**
   * Compares two arrays of objects and checks if they contain the same `id` values.
   *
   * - Only the `id` property of each object is considered.
   * - The order of elements in the arrays does not matter.
   * - If arrays have different lengths or different IDs, they are considered not equal.
   *
   * @template T - The type of objects in the array, which must include an `id` property.
   * @param {T[] | null | undefined} a - The first array of objects.
   * @param {T[] | null | undefined} b - The second array of objects.
   * @returns {boolean} `true` if both arrays contain the same IDs, otherwise `false`.
   */
  arraysHaveSameIds<T extends { id: number | string }>(
    a: T[] | null | undefined,
    b: T[] | null | undefined
  ): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;

    const idsA = a.map((item) => item.id).sort();
    const idsB = b.map((item) => item.id).sort();

    return idsA.every((id, index) => id === idsB[index]);
  }

  /**
   * Normalizes a locale-formatted decimal into an API-safe **string**:
   * uses '.' as decimal separator, strips thousands separators, keeps sign.
   * Returns `null` for empty/invalid input.
   * Needed because BE expects **string** for `cadastralArea` (while `usedArea` is numeric).
   * Examples: "1.234,56" → "1234.56", "12,5" → "12.5"
   */
  normalizeDecimalStringForApi(val: string | number | null | undefined): string | null {
    if (val === null || val === undefined) return null;
    const s = String(val).trim();
    if (!s) return null;

    const lastDot = s.lastIndexOf('.');
    const lastComma = s.lastIndexOf(',');
    const lastSep = Math.max(lastDot, lastComma);

    if (lastSep === -1) {
      const digits = s.replace(/\D/g, '');
      return digits || null;
    }

    const intPart = s.slice(0, lastSep).replace(/\D/g, '');
    const decPart = s.slice(lastSep + 1).replace(/\D/g, '');

    return (intPart || '0') + (decPart ? '.' + decPart : '');
  }

  /**
   * Parses a locale-formatted decimal into a **number** for the BE:
   * accepts ',' or '.' as decimal, removes thousands separators, keeps sign.
   * Returns `null` for empty/invalid input.
   * Used for numeric fields like `usedArea` (whereas `cadastralArea` uses the string normalizer).
   * Examples: "1.234,56" → 1234.56, "12,5" → 12.5
   */
  parseDecimalInputToNumber(val: string | number | null | undefined): number | null {
    if (val === null || val === undefined) return null;
    let s = String(val).trim();
    if (!s) return null;

    let sign = 1;
    if (s.startsWith('-')) {
      sign = -1;
      s = s.slice(1);
    }

    s = s.replace(/[^\d.,]/g, '');

    s = s.replace(/,/g, '.');

    const lastDot = s.lastIndexOf('.');
    if (lastDot !== -1) {
      const intPart = s.slice(0, lastDot).replace(/\./g, '');
      const decPart = s.slice(lastDot + 1);
      s = intPart + (decPart ? '.' + decPart : '');
    }

    const n = Number(s);
    return Number.isFinite(n) ? sign * n : null;
  }
}
